/*
 * Design: Gradient Noir – About page with company story, values, and team
 */
import { Link } from "wouter";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Target, Eye, Heart, Zap, Shield, Users } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

function AnimatedSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollAnimation(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

const values = [
  { icon: Target, title: "מקצועיות", desc: "אנחנו מביאים ידע עמוק וניסיון של שנים לכל פרויקט." },
  { icon: Eye, title: "שקיפות", desc: "תקשורת פתוחה, דיווחים שוטפים ושיתוף פעולה מלא." },
  { icon: Heart, title: "מחויבות", desc: "אנחנו מתייחסים לכל לקוח כאילו זה העסק שלנו." },
  { icon: Zap, title: "חדשנות", desc: "שימוש בטכנולוגיות ושיטות העבודה המתקדמות ביותר." },
  { icon: Shield, title: "אמינות", desc: "יותר מ-20 שנה של תוצאות מוכחות ולקוחות מרוצים." },
  { icon: Users, title: "שותפות", desc: "בניית קשרים ארוכי טווח מבוססי אמון ותוצאות." },
];

export default function About() {
  usePageMeta({
    title: "אודות חברת אפסייט המתמחה בקידום אורגני, בניית אתרים וקידום ממומן",
    description: "אפסייט היא סוכנות בוטיק לשיווק דיגיטלי עם ותק של מעל 20 שנה. אנחנו מאמינים בשקיפות, תוצאות וקשר אישי בגובה העיניים. נלווה אתכם יד ביד עד להשגת תוצאות.",
  });

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-strong" />
        <div className="absolute top-20 right-1/4 w-96 h-96 bg-[#66CDAA]/10 rounded-full blur-[120px]" />
        <div className="container relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 font-[Rubik] leading-tight">
              אודות <span className="gradient-text">אפסייט</span>
            </h1>
            <p className="text-lg text-white/60 leading-relaxed max-w-2xl">
              יותר מ-20 שנה של ניסיון בבניית אתרים וקידום אורגני. אנחנו לא רק ספקים – אנחנו שותפים אמיתיים לצמיחה הדיגיטלית שלכם.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 lg:py-28 section-navy">
        <div className="container">
          <AnimatedSection>
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-8 font-[Rubik]">
                  הסיפור שלנו
                </h2>
                <div className="space-y-5 text-lg text-white/60 leading-relaxed">
                  <p>
                    אפסייט הוקמה מתוך אמונה פשוטה: עסקים ישראליים ראויים לנוכחות דיגיטלית שעובדת באמת. לא אתר "יפה" שלא מביא תוצאות, ולא קידום שמבטיח הרים וגבעות בלי בסיס.
                  </p>
                  <p>
                    לאורך יותר מ-20 שנה, בנינו מאות אתרים וקידמנו אלפי עסקים בגוגל. צברנו ניסיון שאי אפשר לזייף – ניסיון שמתבטא בתוצאות אמיתיות ובלקוחות שנשארים איתנו שנים.
                  </p>
                  <p>
                    היום, אפסייט היא אחת מהחברות המובילות בישראל בתחום בניית אתרים וקידום אורגני, עם צוות מקצועי שמשלב יצירתיות, טכנולוגיה ואסטרטגיה.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#66CDAA]/15 to-transparent rounded-3xl blur-2xl" />
                <img
                  src="https://d2xsxph8kpxj0f.cloudfront.net/310519663396493078/EDyimBtawRo3FYhzWUwDs6/about-team-2CZTGFw7t4tWn2mRVsaqzR.webp"
                  alt="צוות אפסייט"
                  className="relative rounded-2xl shadow-2xl shadow-black/40 border border-white/10"
                  loading="lazy"
                />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28 section-dark relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-l from-transparent via-[#66CDAA]/20 to-transparent" />
        <div className="container">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 font-[Rubik]">
                הערכים <span className="gradient-text">שלנו</span>
              </h2>
              <p className="text-white/50 text-lg max-w-xl mx-auto">
                הערכים שמנחים אותנו בכל פרויקט ובכל אינטראקציה עם לקוחות
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {values.map((val, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div className="glass-card glass-card-hover p-7 transition-all duration-300 h-full">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#66CDAA] to-[#3DD5B0] flex items-center justify-center mb-5">
                    <val.icon className="w-6 h-6 text-[#0F172A]" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 font-[Rubik]">
                    {val.title}
                  </h3>
                  <p className="text-sm text-white/50 leading-relaxed">
                    {val.desc}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-strong" />
        <div className="container relative z-10 text-center">
          <AnimatedSection>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 font-[Rubik]">
              רוצים לדעת עוד?
            </h2>
            <p className="text-lg text-white/60 mb-8 max-w-xl mx-auto">
              נשמח להכיר, לשמוע על העסק שלכם ולהציע פתרונות מותאמים.
            </p>
            <Link href="/contact" className="glow-btn inline-block text-lg px-10 py-4">
              דברו איתנו
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
